"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { FallbackFormField } from "./types";
interface FallbackFormProps {
  fields: FallbackFormField[];
  onRun: (payload: Record<string, unknown> | null) => void;
  /** Optional starting values keyed by field.key. Overrides the default empty strings. */
  initialValues?: Record<string, string>;
  /** Optional render slot shown above the input fields. Receives current form values. */
  renderPreview?: (values: Record<string, string>) => ReactNode;
}

/**
 * Small manual-input form rendered when an archetype (today: HistoricalPicker)
 * has no real data to show. Each archetype that supports a fallback form
 * declares its field set in its registry config via `fallbackFormFields`.
 *
 * Used to escape the dead-end "No data source configured" state for triggers
 * like Gmail when the user has no connector.
 */
export function FallbackForm(t0) {
  const $ = _c(23);
  const {
    fields,
    onRun,
    initialValues,
    renderPreview
  } = t0;
  let t1;
  if ($[0] !== fields || $[1] !== initialValues) {
    t1 = () => {
      const initial = {};
      for (const field of fields) {
        initial[field.key] = initialValues?.[field.key] ?? "";
      }
      return initial;
    };
    $[0] = fields;
    $[1] = initialValues;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [values, setValues] = useState(t1);
  let t2;
  let t3;
  if ($[3] !== renderPreview || $[4] !== values) {
    t3 = renderPreview?.(values) ?? null;
    $[3] = renderPreview;
    $[4] = values;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  t2 = t3;
  const previewNode = t2;
  const onRunRef = useRef(onRun);
  let t4;
  if ($[6] !== onRun) {
    t4 = () => {
      onRunRef.current = onRun;
    };
    $[6] = onRun;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t4);
  let t5;
  let t6;
  if ($[8] !== fields || $[9] !== values) {
    t5 = () => {
      const hasAnyValue = fields.some(field_0 => (values[field_0.key] ?? "").trim().length > 0);
      if (!hasAnyValue) {
        onRunRef.current(null);
        return;
      }
      const payload = {
        triggeredAt: new Date().toISOString()
      };
      for (const field_1 of fields) {
        payload[field_1.key] = values[field_1.key] ?? "";
      }
      onRunRef.current(payload);
    };
    t6 = [values, fields];
    $[8] = fields;
    $[9] = values;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center gap-3"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-px flex-1" /><p className="caption-xs whitespace-nowrap text-center">No real events available — fill in test data manually below.</p><div className="bg-ds-neutral-200 dark:bg-ds-neutral-800 h-px flex-1" /></div>;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== fields || $[14] !== values) {
    let t9;
    if ($[16] !== values) {
      t9 = field_2 => {
        const value = values[field_2.key] ?? "";
        if (field_2.type === "textarea") {
          return <label key={field_2.key} className="block"><span className="field-label">{field_2.label}</span><textarea value={value} onChange={e => setValues(prev => ({
              ...prev,
              [field_2.key]: e.target.value
            }))} placeholder={field_2.placeholder} rows={4} className="border-ds-neutral-300 text-ds-text-primary placeholder:text-ds-text-disabled dark:border-ds-neutral-700 bg-ds-surface-card mt-1 w-full resize-y rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />{field_2.description && <p className="caption-xs mt-1">{field_2.description}</p>}</label>;
        }
        return <label key={field_2.key} className="block"><span className="field-label">{field_2.label}</span><input type={field_2.type === "email" ? "email" : "text"} value={value} onChange={e_0 => setValues(prev_0 => ({
            ...prev_0,
            [field_2.key]: e_0.target.value
          }))} placeholder={field_2.placeholder} className="border-ds-neutral-300 text-ds-text-primary placeholder:text-ds-text-disabled dark:border-ds-neutral-700 bg-ds-surface-card mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />{field_2.description && <p className="caption-xs mt-1">{field_2.description}</p>}</label>;
      };
      $[16] = values;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    t8 = fields.map(t9);
    $[13] = fields;
    $[14] = values;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <div className="flex flex-col gap-3">{t8}</div>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== previewNode || $[21] !== t9) {
    t10 = <div className="space-y-3">{previewNode}{t7}{t9}</div>;
    $[20] = previewNode;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}